import {
  sortByDateTime,
  Avatar,
  Spacer,
  Tag,
  LaunchPage,
  TableTagTransaction,
  MaterialUiTable,
  usePersistMuiTableState,
  MaterialUiColumnsProps,
  TextCapitalized,
  dateFormatterDayMonthYearLong2Digits,
  dateFormatterHourMinute,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import {
  PublicTransactionsPropsType,
  PublicTransactionDisplayed,
  TransactionStatus,
  MerchantGroupsList,
} from "../transactionsAPI";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  selectAuthorizationsList,
  selectAuthorizationsListStatus,
  selectTransactionsList,
  selectTransactionsListStatus,
} from "../transactionsSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getCategoryName } from "../../rules/libDefaultDatas";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import styled from "styled-components";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { LittleCardAsset } from "@qivia/ui/src/assets/assets";
import { documentDownloadXlsxAsync } from "../../../../libSlice/downloadFileXslx";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import {
  formatCurrency,
  formatDate,
  formatMileage,
  getDate,
} from "../../../../libSlice/materialUiFormats";
import { merchantIcons } from "../../../../libSlice/merchantGroups";
import "../../../../i18n/i18n";
import { fakeTransactionsDisplayedForDemo } from "../lib";
import { useNavigate, useParams } from "react-router-dom";

interface SharedInterfaceCardTransactionsProps {
  sidePanelAction?: (props: Partial<PublicTransactionsPropsType>) => void;
  sidePanel?: JSX.Element;
}

export const SharedInterfaceCardTransactions = (
  props: SharedInterfaceCardTransactionsProps,
) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const [listDisplayed, setListDisplayed] = useState<
    PublicTransactionDisplayed[] | null
  >(null);
  const transactionsList: PublicTransactionsPropsType[] | null = useAppSelector(
    selectTransactionsList,
  );
  const authorizationsRefusedList: PublicTransactionsPropsType[] | null =
    useAppSelector(selectAuthorizationsList);
  const [rowHoverUuid, setRowHoverUuid] = useState<string | null>(null);
  const transactionsListStatus = useAppSelector(selectTransactionsListStatus);
  const authorizationsListStatus = useAppSelector(
    selectAuthorizationsListStatus,
  );

  const pageName = "transactions";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      amount: "size",
      merchantName: "size",
      category: "size",
      driverName: "size",
      status: "size",
      supportingDocumentStatus: "size",
      mileage: "avg",
      quantity: "sum",
    },
  };

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const keysToTranslate = useMemo(
    () =>
      [
        "status",
        "category",
        "date",
        "time",
        "supportingDocumentStatus",
        "merchantGroup",
        "fuel",
      ] as (keyof PublicTransactionDisplayed)[],
    [],
  );

  const isDemoEnvironment = useCallback(() => {
    return import.meta.env["VITE_TARGET_ENV"] === "demo";
  }, []);

  const findKeysToTranslate = useCallback(
    (key: keyof PublicTransactionsPropsType, value: string) => {
      switch (key) {
        case "status":
          return t(`transactions.status.${value}`);
        case "category":
          return t(`transactions.category.${value}`);
        case "date":
          return dateFormatterDayMonthYearLong2Digits(new Date(value));
        case "time":
          return dateFormatterHourMinute(new Date(value));
        case "supportingDocumentStatus":
          return t(`transactions.supportingDocument.${value}`);
        case "merchantGroup":
          return t(`merchantGroups.${value}`);
        case "fuel":
          return t(`transactions.fuel.${value}`);
        default:
          return value;
      }
    },
    [t],
  );

  const allTransactionsDisplayed: PublicTransactionsPropsType[] =
    useMemo(() => {
      if (!transactionsList || !authorizationsRefusedList) return [];
      const list = [...transactionsList, ...authorizationsRefusedList];
      const listDisplayed = isDemoEnvironment()
        ? [...list, ...fakeTransactionsDisplayedForDemo]
        : list;
      const listSorted = sortByDateTime(listDisplayed, "desc");

      const rowsWithId = listSorted.map((av) => {
        let newDict: PublicTransactionsPropsType & {
          id: string;
        } = {
          ...av,
          id: av.uuid,
        };
        Object.entries(av).forEach(([key, value]) => {
          if (
            keysToTranslate.includes(key as keyof PublicTransactionDisplayed)
          ) {
            newDict = {
              ...newDict,
              [key]: value
                ? findKeysToTranslate(
                    key as keyof PublicTransactionsPropsType,
                    value as string,
                  )
                : "",
            };
          }
        });
        return newDict;
      });
      return rowsWithId;
    }, [
      authorizationsRefusedList,
      findKeysToTranslate,
      isDemoEnvironment,
      keysToTranslate,
      transactionsList,
    ]);

  useEffect(() => {
    if (
      allTransactionsDisplayed &&
      transactionsListStatus === "success" &&
      authorizationsListStatus === "success"
    ) {
      setListDisplayed(allTransactionsDisplayed);
    }
  }, [
    allTransactionsDisplayed,
    authorizationsListStatus,
    transactionsListStatus,
  ]);

  useEffect(() => {
    if (params.id) {
      const transactionSelected = allTransactionsDisplayed.find(
        (t) => t.uuid === params.id,
      );
      if (transactionSelected) {
        const firstPath = location.pathname.split("/")[1]; // home or departments
        navigate(
          `/${firstPath}/transactions/cards/${transactionSelected.uuid}`,
        );
      }
    }
  }, [allTransactionsDisplayed, navigate, params.id]);

  const searchBarProps = {
    values: allTransactionsDisplayed,
    setFilterValues: setListDisplayed,
    keysToIgnore: [
      "uuid" as const,
      "merchantCity" as const,
      "cardLast4Digits" as const,
      "reasonsRefused" as const,
      "consumption" as const,
      "supportingDocumentUuid" as const,
      "driverPhone" as const,
      "driver" as const,
      "driverLastName" as const,
      "ruleName" as const,
      "ruleUuid" as const,
      "vehicleUuid" as const,
      "cardUuid" as const,
      "carbonEmission" as const,
      "areSupportingDocumentsMandatory" as const,
    ],
  };

  const columnsDisplayed: {
    [index in keyof PublicTransactionDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      type: {
        type: "string",
        width: 60,
        valueFormatter: () => {
          return (
            <StyledRow>
              <LittleCardAsset />
            </StyledRow>
          );
        },
      },
      date: {
        type: "date",
        width: 140,
        valueGetter: getDate,
        valueFormatter: formatDate,
      },
      time: {
        type: "string",
        width: 100,
        valueFormatter: (str: string) => str?.replace(":", "h") ?? null,
      },
      merchantName: {
        type: "string",
        width: 200,
      },
      merchantGroup: {
        type: "string",
        width: 200,
        valueFormatter: (value: string) => {
          if (!value) return <>-</>;
          const merchant = MerchantGroupsList.find(
            (m) => t(`merchantGroups.${m}`) === value,
          );
          return (
            value && (
              <StyledRow>
                {merchantIcons[merchant as keyof typeof merchantIcons]}
                <Spacer x={0.75} />
                <TextCapitalized>{value}</TextCapitalized>
              </StyledRow>
            )
          );
        },
      },
      category: {
        type: "string",
        valueFormatter: (value, row) => {
          const merchantCategory = getCategoryName(value);
          return (
            value && (
              <StyledRow>
                <Tag
                  text={merchantCategory}
                  backgroundColor={
                    colors["colors/surfaces/background/background_level2"]
                  }
                  textColor={colors["colors/text/black"]}
                  colorHover={
                    rowHoverUuid === row["uuid"]
                      ? colors["colors/surfaces/background/background_level0"]
                      : undefined
                  }
                />
              </StyledRow>
            )
          );
        },
      },
      driverName: {
        type: "string",
        width: 250,
        valueFormatter: (value: string, row) => {
          if (!value) return <>-</>;
          const initials = value?.split(" ").map((row) => row.charAt(0));
          return (
            value && (
              <StyledRow>
                <Avatar
                  color={"mint"}
                  size={"S"}
                  firstName={initials[0]}
                  lastName={initials[initials.length - 1]}
                  isHover={rowHoverUuid === row["uuid"]}
                />
                <Spacer x={0.75} />
                {value}
              </StyledRow>
            )
          );
        },
      },
      status: {
        type: "string",
        valueFormatter: (value: TransactionStatus, row) =>
          value && (
            <StyledRow>
              <TableTagTransaction
                status={value}
                isRowHover={rowHoverUuid === row["uuid"]}
              />
            </StyledRow>
          ),
      },
      supportingDocumentStatus: {
        type: "string",
        valueFormatter: (value: string, row) => {
          return (
            value && (
              <StyledRow>
                <Tag
                  text={value}
                  backgroundColor={
                    value === t("transactions.supportingDocument.ACCEPTED")
                      ? colors["colors/system/success/success_ultraLight"]
                      : colors["colors/surfaces/background/background_level2"]
                  }
                  textColor={
                    value === t("transactions.supportingDocument.ACCEPTED")
                      ? colors["colors/system/success/success_normal"]
                      : colors["colors/text/black"]
                  }
                  colorHover={
                    value !== t("transactions.supportingDocument.ACCEPTED") &&
                    rowHoverUuid === row["uuid"]
                      ? colors["colors/surfaces/background/background_level0"]
                      : undefined
                  }
                />
              </StyledRow>
            )
          );
        },
      },
      amount: {
        type: "number",
        valueFormatter: (value: number, row) => {
          const isRefund = row.type === "REFUND";
          const isCanceled = row.status === t("transactions.status.CANCELED");
          return (
            value && (
              <StyledAmount $isCanceled={isCanceled} $isRefund={isRefund}>
                {isRefund ? "+" : ""}
                {formatCurrency(Math.abs(value))}
              </StyledAmount>
            )
          );
        },
      },
      mileage: {
        type: "number",
        valueFormatter: formatMileage,
      },
      registrationNumber: {
        type: "string",
        width: 200,
      },
      vehicleReference: {
        type: "string",
        width: 200,
      },
      vehicleAnalyticCode: {
        type: "string",
        width: 200,
      },
      fuel: {
        type: "string",
        width: 200,
        valueFormatter: (value: string) => {
          return <TextCapitalized>{value}</TextCapitalized>;
        },
      },
      quantity: {
        type: "number",
        width: 200,
      },
    };
  }, [rowHoverUuid, t]);

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName: "QIVIA_" + t("transactions.title") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  return (
    <>
      <MaterialUiTable<
        keyof PublicTransactionDisplayed,
        PublicTransactionDisplayed
      >
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        onRowClickAction={props.sidePanelAction}
        setRowHoverUuid={setRowHoverUuid}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
        downloadViewAction={downloadViewAction}
      />
      {props.sidePanel ? props.sidePanel : <></>}
    </>
  );
};

const StyledAmount = styled.div<{
  $isCanceled?: boolean;
  $isRefund?: boolean;
}>`
  text-decoration: ${({ $isCanceled }) =>
    $isCanceled ? "line-through" : "none"};
  ${typographies["Body/L"]}
  height: 100%;
  color: ${({ $isRefund }) =>
    $isRefund && colors["colors/system/success/success_normal"]};
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;
